import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useCommand} from 'hooks';
import {useEntityContext} from 'components/EntityPage/EntityContext';
import {AssignedRequirementWithID} from '../../AssignedEntities/AssignedRequirementColumns';
import {Regulation} from 'types';
import {useParams} from 'react-router-dom';
import {ProjectDetailsPageParams} from '../../vehicleProjectPagePaths';
import {ProjectRelevanceDialog} from './ProjectRelevanceDialog';

export const ProjectRelevanceButton: React.FC = () => {
	const {t} = useTranslation('features/vehicleprojects', {
		keyPrefix: 'ProjectRelevantFields',
	});
	const {selectedItems} = useEntityContext<
		AssignedRequirementWithID | Regulation
	>();
	const {modelSeries, generation} = useParams() as ProjectDetailsPageParams;
	const [isDialogOpen, setIsDialogOpen] = useState(false);

	const handleProjectRelevanceClick = React.useCallback(() => {
		setIsDialogOpen(true);
	}, []);

	const handleDialogClose = React.useCallback(() => {
		setIsDialogOpen(false);
	}, []);

	let dataType: 'unknown' | 'regulation' | 'requirement' | 'regDoc' = 'unknown';

	if (selectedItems.length > 0) {
		if ('regulationNumber' in selectedItems[0]) {
			dataType = 'regulation';
		} else if (
			(selectedItems[0] as any).regulation &&
			'regulationNumber' in (selectedItems[0] as any).regulation
		) {
			dataType = 'regDoc';
		} else {
			dataType = 'requirement';
		}
	}

	useCommand(
		{
			key: 'projectRelevance',
			text: t('ProjectRelevanceBtn'),
			iconProps: {iconName: 'TaskLogo'},
			priority: 6,
			disabled: selectedItems.length === 0,
			onClick: handleProjectRelevanceClick,
		},
		[selectedItems, handleProjectRelevanceClick, t],
	);

	const singleSelectedItem =
		selectedItems.length === 1 ? selectedItems[0] : null;

	// For requirements, we can show existing values
	const initialIsProjectRelevant =
		dataType === 'requirement' && singleSelectedItem
			? (singleSelectedItem as AssignedRequirementWithID).isProjectRelevant
			: true;
	const initialComments =
		dataType === 'requirement'  && singleSelectedItem
			? (singleSelectedItem as AssignedRequirementWithID)
					.projectRelevantComments
			: '';

	const regulationIds = useMemo(() => {
		if (dataType === 'regulation') {
			return selectedItems.map(item => item.id);
		}
		if (dataType === 'regDoc') {
			return selectedItems.map(item => (item as any).regulation.id);
		}
		return [];
	}, [dataType, selectedItems]);

	return (
		<ProjectRelevanceDialog
			isOpen={isDialogOpen}
			onClose={handleDialogClose}
			modelSeries={modelSeries}
			generation={generation}
			requirementIds={
				dataType === 'requirement' ? selectedItems.map(item => item.id) : []
			}
			regulationIds={regulationIds}
			initialIsProjectRelevant={initialIsProjectRelevant}
			initialComments={initialComments}
		/>
	);
};
